import { z } from "zod";
import { LOT_MAX_COLUMNS, LOT_MAX_DATA_ROWS, LOT_MAX_SHEET_ROWS, lotFieldSchema, lotStoredFieldSchema, } from "./lot-mapping.js";
/**
 * "Reparar planilha": fixes the person confirms, one by one or in bulk, for values the converter
 * blocks. A repair is never applied on its own and never invents a value: it picks one of the
 * candidates written in the cell, one the file itself holds (another column, a catalog code) or a
 * value the person typed. The confirmed repair is kept on the upload receipt.
 */
export const LOT_MAX_REPAIRS = 2000;
export const lotRepairKindSchema = z.enum([
    /** "1901.90.90/2101.11.00": several NCMs in one cell; the person picks one. */
    "NCM_PICK",
    /** A text NCM of 7 digits whose leading zero was lost, restored when the result is a catalog NCM. */
    "NCM_LEADING_ZERO",
    /** A UF token that is not a UF ("VALIDAR", "S.P.", a misspelled state): replaced by a valid UF. */
    "UF_REPLACE",
    /** A cost cell with a cached Excel error: the value of another cost column on the same row. */
    "COST_ALTERNATIVE_COLUMN",
    /** A cost cell with a cached Excel error and no other source: the value the person typed. */
    "COST_MANUAL",
    /**
     * A CEST that no published rule states for the row's NCM and route, while rules for that NCM and
     * route state other CESTs: one of those CESTs, applied to every row with the same NCM and CEST.
     */
    "CEST_REPLACE",
    /**
     * An NCM the person chose from the official catalog (product → NCM search) for an NCM cell the
     * file cannot mend by itself. Never suggested or applied in bulk: always one explicit
     * confirmation. The code must be an active catalog NCM when the file is imported, and the receipt
     * keeps the search the person ran.
     */
    "NCM_REPLACE",
]);
const lotRepairObject = z
    .object({
    kind: lotRepairKindSchema,
    sheet: z.string().min(1).max(100),
    /** 1-based worksheet row, as numbered in the file. */
    row: z.number().int().positive().max(LOT_MAX_SHEET_ROWS),
    /** 0-based column of the cell. */
    column: z
        .number()
        .int()
        .min(0)
        .max(LOT_MAX_COLUMNS - 1),
    field: lotFieldSchema,
    /** What the cell shows now: its text, or the cached error literal such as "#REF!". */
    original: z.string().max(200),
    /** The value that replaces it: an 8-digit NCM, a UF code, a CEST or a plain decimal amount. */
    replacement: z.string().min(1).max(60),
    /** COST_ALTERNATIVE_COLUMN: the 0-based column the amount is read from, on the same row. */
    fromColumn: z
        .number()
        .int()
        .min(0)
        .max(LOT_MAX_COLUMNS - 1)
        .optional(),
    /** NCM_REPLACE: what the person typed in the product → NCM search, for the audit. */
    searchQuery: z.string().trim().min(1).max(120).optional(),
})
    .strict();
/** NCM_REPLACE writes an 8-digit NCM into the NCM column; only it records a search query. */
function checkNcmReplace(repair, context) {
    if (repair.kind === "NCM_REPLACE" && !/^\d{8}$/.test(repair.replacement)) {
        context.addIssue({
            code: "custom",
            path: ["replacement"],
            message: "NCM_REPLACE writes an 8-digit NCM",
        });
    }
    if (repair.kind === "NCM_REPLACE" && repair.field !== "ncm") {
        context.addIssue({ code: "custom", path: ["field"], message: "NCM_REPLACE repairs the NCM" });
    }
    if (repair.kind !== "NCM_REPLACE" && repair.searchQuery !== undefined) {
        context.addIssue({
            code: "custom",
            path: ["searchQuery"],
            message: "Only an NCM_REPLACE records a search query",
        });
    }
}
/** One cell replaced. The server checks it against the cell it names before using it. */
export const lotRepairSchema = lotRepairObject.superRefine(checkNcmReplace);
/** A repair as kept on the upload receipt: what, where, original to new value, and who confirmed it. */
const lotRepairRecordObject = lotRepairObject
    .extend({
    confirmedBy: z.string().uuid(),
    /** The e-mail or name of the person, so the audit reads without a lookup. */
    confirmedByName: z.string().max(200),
    confirmedAt: z.string().datetime(),
})
    .strict();
export const lotRepairRecordSchema = lotRepairRecordObject.superRefine(checkNcmReplace);
/** A future receipt may name a new field, but the legacy repair writer stays closed. */
export const lotStoredRepairRecordSchema = lotRepairRecordObject
    .extend({ field: lotStoredFieldSchema })
    .superRefine(checkNcmReplace);
export const lotRepairCandidateReasonSchema = z.enum([
    /** In the NCM catalog and the closest to the row's description. */
    "CATALOG_DESCRIPTION",
    /** In the NCM catalog. */
    "CATALOG",
    /** Closest to the row's description, the catalog was not available. */
    "DESCRIPTION",
    /** Only its format is valid; the catalog could not confirm it. */
    "FORMAT",
    /** The closest UF to what was written. */
    "CLOSEST_UF",
    /** The amount another column of the same row holds. */
    "ALTERNATIVE_COLUMN",
    /** The CEST a published rule in force states for the row's NCM and route. */
    "PUBLISHED_RULE",
]);
export const lotRepairCandidateSchema = z
    .object({
    value: z.string().min(1).max(60),
    /** The catalog description of an NCM, or the state name of a UF. */
    label: z.string().max(300).optional(),
    /** 0..1, how well the candidate fits; candidates are listed best first. */
    score: z.number().min(0).max(1),
    reason: lotRepairCandidateReasonSchema,
    /** True or false when the NCM catalog was consulted, absent when it was not. */
    inCatalog: z.boolean().optional(),
})
    .strict();
export const lotRepairAlternativeColumnSchema = z
    .object({
    column: z
        .number()
        .int()
        .min(0)
        .max(LOT_MAX_COLUMNS - 1),
    columnLabel: z.string().max(4),
    header: z.string().max(200),
    /** How many of the affected rows have a usable amount in it. */
    rows: z.number().int().nonnegative().max(LOT_MAX_DATA_ROWS),
    /** The amount of each of those rows, as plain decimal text: what the repair would write. */
    values: z
        .array(z
        .object({
        row: z.number().int().positive().max(LOT_MAX_SHEET_ROWS),
        value: z.string().min(1).max(60),
    })
        .strict())
        .max(LOT_MAX_DATA_ROWS),
})
    .strict();
/** One thing to repair: every row of a sheet that carries the same value in the same column. */
export const lotRepairIssueSchema = z
    .object({
    /** Stable for the same value in the same column, so a decision survives a new suggestion. */
    id: z.string().min(1).max(120),
    kind: lotRepairKindSchema,
    sheet: z.string().max(100),
    column: z
        .number()
        .int()
        .min(0)
        .max(LOT_MAX_COLUMNS - 1),
    columnLabel: z.string().max(4),
    header: z.string().max(200),
    field: lotFieldSchema,
    original: z.string().max(200),
    /** The rows (1-based) that carry this value. */
    rows: z
        .array(z.number().int().positive().max(LOT_MAX_SHEET_ROWS))
        .min(1)
        .max(LOT_MAX_DATA_ROWS),
    candidates: z.array(lotRepairCandidateSchema).max(10),
    /** Cost errors only: other columns that hold an amount for these rows. */
    alternativeColumns: z.array(lotRepairAlternativeColumnSchema).max(10).optional(),
    /** The person may type a value of their own (always true for costs; for UFs any UF code). */
    allowsManual: z.boolean(),
})
    .strict();
export const lotRepairSuggestionsSchema = z
    .object({
    issues: z.array(lotRepairIssueSchema).max(200),
    /** False when the NCM catalog could not be consulted: candidates are ranked by description only. */
    catalogChecked: z.boolean(),
    /** Rows with blocking inputs that the offered cell repairs cannot settle.
     * Present for explicit commercial declarations; contains no commercial values. */
    pendingInputRows: z.array(z.number().int().positive().max(5025)).max(5000).optional(),
    pendingInputFields: z.array(lotFieldSchema).optional(),
})
    .strict();
//# sourceMappingURL=lot-repair.js.map